import { FormEvent, useEffect, useMemo, useState } from 'react'
import { Download, Loader2, MessageCircle, RefreshCw, Search } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { apiService, ZapLeadActivityType, ZapLeadProspectDetail, ZapLeadProspectItem, ZapLeadQueue, ZapLeadRun, ZapLeadRunStatus, ZapLeadSort, ZapLeadStatus } from '@/services/api'

type ResultStatus = 'all' | ZapLeadStatus
type ResultScope = 'run' | 'global'
type QueueFilter = 'all' | ZapLeadQueue
type SignalFilter = 'all' | 'has_contact' | 'has_creci' | 'existing_tenant'

const STATUS_OPTIONS: ResultStatus[] = [
  'all',
  'novo',
  'qualificando',
  'contatado',
  'interessado',
  'sem_perfil',
  'cliente_existente',
  'indisponivel',
  'erro',
]
const QUEUE_OPTIONS: QueueFilter[] = ['all', 'prioridade_a', 'prioridade_b', 'prioridade_c', 'clientes']
const SIGNAL_OPTIONS: SignalFilter[] = ['all', 'has_contact', 'has_creci', 'existing_tenant']
const SORT_OPTIONS: ZapLeadSort[] = ['updated_desc', 'score_desc', 'evidence_desc']
const ACTIVITY_TYPE_OPTIONS: ZapLeadActivityType[] = ['whatsapp', 'ligacao', 'convite_enviado', 'nota']
const TEMPERATURE_OPTIONS = ['', 'frio', 'morno', 'quente', 'muito_quente'] as const
const REASON_OPTIONS = [
  '',
  'quero_parceria',
  'quero_vender',
  'quero_captar',
  'quero_divulgar',
  'quero_mls',
  'quero_crm',
  'nao_entendeu',
  'sem_tempo',
  'sem_perfil',
  'erro',
] as const
const ACTIVATION_ACTION_OPTIONS = [
  'primeiro_acesso',
  'perfil_completo',
  'imovel_publicado',
  'imovel_revisado',
  'vitrine_acessada',
  'parceria_solicitada',
  'parceria_aceita',
] as const

const CURRENT_RUN_STORAGE_KEY = 'zap-leads-current-run-id'
const RUN_STATUS_LABELS: Record<ZapLeadRunStatus, string> = {
  QUEUED: 'Na fila',
  RUNNING: 'Em execução',
  DONE: 'Concluída',
  PARTIAL: 'Parcial',
  FAILED: 'Falhou',
  CANCELLED: 'Interrompida',
}
const SOURCE_HEALTH_LABELS: Record<string, string> = {
  stable: 'Estável',
  attention: 'Atenção',
  blocked: 'Bloqueada',
}
const PORTFOLIO_STATUS_LABELS: Record<string, string> = {
  available: 'Disponível',
  partial: 'Parcial',
  empty: 'Vazia',
}
const QUEUE_LABELS: Record<string, string> = {
  prioridade_a: 'Prioridade A',
  prioridade_b: 'Prioridade B',
  prioridade_c: 'Prioridade C',
  dormindo: 'Dormindo',
  reativacao: 'Reativação',
  duplicados: 'Duplicados',
  clientes: 'Cadastrados',
  ia: 'IA',
}
const SIGNAL_LABELS: Record<SignalFilter, string> = {
  all: 'Todos sinais',
  has_contact: 'Com contato',
  has_creci: 'Com CRECI',
  existing_tenant: 'Cadastrado na Rede',
}
const SORT_LABELS: Record<ZapLeadSort, string> = {
  updated_desc: 'Mais recentes',
  score_desc: 'Score comercial',
  evidence_desc: 'Evidências',
}
const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  contato: 'Contato',
  whatsapp: 'WhatsApp',
  ligacao: 'Ligação',
  convite_enviado: 'Convite enviado',
  nota: 'Nota',
  cadastro: 'Cadastro',
  ativacao: 'Ativação',
  score: 'Score',
  fila: 'Fila',
  temperatura: 'Temperatura',
  addon: 'Addon',
}
const TEMPERATURE_LABELS: Record<string, string> = {
  frio: 'Frio',
  morno: 'Morno',
  quente: 'Quente',
  muito_quente: 'Muito quente',
}
const REASON_LABELS: Record<string, string> = {
  quero_parceria: 'Quer parceria',
  quero_vender: 'Quer vender',
  quero_captar: 'Quer captar',
  quero_divulgar: 'Quer divulgar',
  quero_mls: 'Quer MLS',
  quero_crm: 'Quer CRM',
  nao_entendeu: 'Não entendeu',
  sem_tempo: 'Sem tempo',
  sem_perfil: 'Sem perfil',
  erro: 'Erro operacional',
}
const ACTIVATION_ACTION_LABELS: Record<string, string> = {
  primeiro_acesso: 'Primeiro acesso',
  perfil_completo: 'Perfil completo',
  imovel_publicado: 'Imóvel publicado',
  imovel_revisado: 'Imóvel revisado',
  vitrine_acessada: 'Vitrine acessada',
  parceria_solicitada: 'Parceria solicitada',
  parceria_aceita: 'Parceria aceita',
}

function formatDateTime(value?: string | null): string {
  if (!value) return '-'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '-'
  return parsed.toLocaleString('pt-BR')
}

function statusLabel(status: ResultStatus): string {
  const labels: Record<ResultStatus, string> = {
    all: 'Todos',
    novo: 'Novo',
    qualificando: 'Qualificando',
    contatado: 'Contatado',
    interessado: 'Interessado',
    sem_perfil: 'Sem perfil',
    cliente_existente: 'Cadastrado na Rede',
    indisponivel: 'Indisponivel',
    erro: 'Erro',
  }
  return labels[status] ?? status
}

function runStatusLabel(status: ZapLeadRun['status']): string {
  return RUN_STATUS_LABELS[status as ZapLeadRunStatus] ?? String(status || '-')
}

function sourceHealthLabel(status?: string | null): string {
  return SOURCE_HEALTH_LABELS[status || 'stable'] ?? String(status || '-')
}

function portfolioStatusLabel(status?: string | null): string {
  return PORTFOLIO_STATUS_LABELS[status || 'partial'] ?? String(status || 'Parcial')
}

function queueLabel(queue?: string | null): string {
  return queue ? QUEUE_LABELS[queue] ?? queue : '-'
}

function signalLabel(signal: SignalFilter): string {
  return SIGNAL_LABELS[signal]
}

function activityTypeLabel(activityType?: string | null): string {
  return activityType ? ACTIVITY_TYPE_LABELS[activityType] ?? activityType : '-'
}

function temperatureLabel(temperature?: string | null): string {
  return temperature ? TEMPERATURE_LABELS[temperature] ?? temperature : '-'
}

function reasonLabel(reason?: string | null): string {
  return reason ? REASON_LABELS[reason] ?? reason : '-'
}

function activationActionLabel(action?: string | null): string {
  return action ? ACTIVATION_ACTION_LABELS[action] ?? action : '-'
}

function activationActionsLabel(actions?: string[] | null): string {
  return actions?.length ? actions.map(activationActionLabel).join(', ') : '-'
}

function whatsappHref(phone?: string | null): string | null {
  const digits = (phone || '').replace(/\D/g, '')
  if (!digits) return null
  return `https://wa.me/${digits.startsWith('55') ? digits : `55${digits}`}`
}

function formatPhone(phone?: string | null): string {
  return phone || '-'
}

function formatMoney(value?: number | null): string {
  return typeof value === 'number' ? `R$ ${Math.round(value).toLocaleString('pt-BR')}` : '-'
}

function formatPriceRange(range?: { min?: number | null; max?: number | null } | null): string | null {
  if (!range || typeof range.min !== 'number' || typeof range.max !== 'number') return null
  if (range.min === range.max) return formatMoney(range.min)
  return `${formatMoney(range.min)} - ${formatMoney(range.max)}`
}

function evidenceLocation(city?: string | null, neighborhood?: string | null): string {
  return [neighborhood, city].filter(Boolean).join(' - ') || '-'
}

function errorSnapshotMessage(snapshot: unknown): string | null {
  if (!snapshot || typeof snapshot !== 'object') return null
  const message = (snapshot as { message?: unknown }).message
  return typeof message === 'string' && message.trim() ? message : null
}

export function ZapLeadsPage() {
  const [stateAcronym, setStateAcronym] = useState('')
  const [addressCity, setAddressCity] = useState('')
  const [addressNeighborhood, setAddressNeighborhood] = useState('')
  const [addressZone, setAddressZone] = useState('')
  const [addressStreet, setAddressStreet] = useState('')
  const [unitType, setUnitType] = useState('APARTMENT')
  const [priceMin, setPriceMin] = useState('')
  const [priceMax, setPriceMax] = useState('')
  const [usableAreaMin, setUsableAreaMin] = useState('')
  const [usableAreaMax, setUsableAreaMax] = useState('')
  const [bedrooms, setBedrooms] = useState('')
  const [parkingSpaces, setParkingSpaces] = useState('')
  const [business, setBusiness] = useState('SALE')
  const [listingType, setListingType] = useState('USED')
  const [targetProspects, setTargetProspects] = useState(100)
  const [maxPages, setMaxPages] = useState(10)
  const [maxEnrichments, setMaxEnrichments] = useState(40)

  const [searching, setSearching] = useState(false)
  const [loadingResults, setLoadingResults] = useState(false)
  const [loadingRuns, setLoadingRuns] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [currentRun, setCurrentRun] = useState<ZapLeadRun | null>(null)
  const [runs, setRuns] = useState<ZapLeadRun[]>([])
  const [results, setResults] = useState<ZapLeadProspectItem[]>([])
  const [resultPage, setResultPage] = useState(1)
  const [resultPageSize, setResultPageSize] = useState(25)
  const [resultTotal, setResultTotal] = useState(0)
  const [statusFilter, setStatusFilter] = useState<ResultStatus>('all')
  const [queueFilter, setQueueFilter] = useState<QueueFilter>('all')
  const [signalFilter, setSignalFilter] = useState<SignalFilter>('all')
  const [prospectSort, setProspectSort] = useState<ZapLeadSort>('updated_desc')
  const [resultScope, setResultScope] = useState<ResultScope>('run')
  const [selectedProspect, setSelectedProspect] = useState<ZapLeadProspectDetail | null>(null)
  const [loadingProspectId, setLoadingProspectId] = useState<number | null>(null)
  const [savingActivity, setSavingActivity] = useState(false)
  const [activityType, setActivityType] = useState<ZapLeadActivityType>('whatsapp')
  const [activityTemperature, setActivityTemperature] = useState('')
  const [activityReason, setActivityReason] = useState('')
  const [activityNextActionAt, setActivityNextActionAt] = useState('')
  const [activityInviteLink, setActivityInviteLink] = useState('')
  const [activityNote, setActivityNote] = useState('')
  const [savingRegistration, setSavingRegistration] = useState(false)
  const [registrationTenantId, setRegistrationTenantId] = useState('')
  const [savingActivation, setSavingActivation] = useState(false)
  const [activationFirstValueAction, setActivationFirstValueAction] = useState('primeiro_acesso')

  const totalResultPages = useMemo(() => {
    if (!resultTotal) return 0
    return Math.max(1, Math.ceil(resultTotal / resultPageSize))
  }, [resultTotal, resultPageSize])

  const activeFiltersCount = useMemo(() => {
    return [stateAcronym, addressCity, addressNeighborhood, addressZone, addressStreet]
      .map((value) => value.trim())
      .filter(Boolean)
      .length
  }, [stateAcronym, addressCity, addressNeighborhood, addressZone, addressStreet])

  const buildSearchPayload = () => {
    const normalizedState = stateAcronym.trim().toUpperCase()
    return {
      ...(normalizedState ? { stateAcronym: normalizedState } : {}),
      ...(addressCity.trim() ? { addressCity: addressCity.trim() } : {}),
      ...(addressNeighborhood.trim() ? { addressNeighborhood: addressNeighborhood.trim() } : {}),
      ...(addressZone.trim() ? { addressZone: addressZone.trim() } : {}),
      ...(addressStreet.trim() ? { addressStreet: addressStreet.trim() } : {}),
      ...(unitType.trim() ? { unitType: unitType.trim() } : {}),
      ...(priceMin.trim() ? { priceMin: Number(priceMin) } : {}),
      ...(priceMax.trim() ? { priceMax: Number(priceMax) } : {}),
      ...(usableAreaMin.trim() ? { usableAreasMin: Number(usableAreaMin) } : {}),
      ...(usableAreaMax.trim() ? { usableAreasMax: Number(usableAreaMax) } : {}),
      ...(bedrooms.trim() ? { bedrooms: Number(bedrooms) } : {}),
      ...(parkingSpaces.trim() ? { parkingSpaces: Number(parkingSpaces) } : {}),
      business: business || 'SALE',
      listingType: listingType || 'USED',
      target_prospects: Math.max(1, targetProspects),
      max_pages: Math.max(1, maxPages),
      size: 25,
      max_enrichments: Math.max(1, maxEnrichments),
    }
  }

  const signalPayload = (signal: SignalFilter) => ({
    ...(signal === 'has_contact' ? { hasContact: true } : {}),
    ...(signal === 'has_creci' ? { hasCreci: true } : {}),
    ...(signal === 'existing_tenant' ? { existingTenant: true } : {}),
  })

  const loadResults = async (
    runId: number,
    page: number,
    status?: ResultStatus,
    queue?: QueueFilter,
    signal = signalFilter,
    sort = prospectSort,
  ) => {
    setLoadingResults(true)
    try {
      const payload = await apiService.getZapLeadResults(runId, {
        page,
        pageSize: resultPageSize,
        ...(status && status !== 'all' ? { status } : {}),
        ...(queue && queue !== 'all' ? { queue } : {}),
        ...signalPayload(signal),
        sort,
      })
      setSelectedProspect(null)
      setResults(payload.items || [])
      setResultPage(payload.page || 1)
      setResultTotal(payload.total || 0)
      setResultPageSize(payload.pageSize || 25)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Falha ao carregar prospects')
    } finally {
      setLoadingResults(false)
    }
  }

  const loadGlobalProspects = async (
    page: number,
    status?: ResultStatus,
    queue?: QueueFilter,
    signal = signalFilter,
    sort = prospectSort,
  ) => {
    setLoadingResults(true)
    try {
      const payload = await apiService.getZapLeadProspects({
        page,
        pageSize: resultPageSize,
        ...(status && status !== 'all' ? { status } : {}),
        ...(queue && queue !== 'all' ? { queue } : {}),
        ...signalPayload(signal),
        sort,
      })
      setSelectedProspect(null)
      setResults(payload.items || [])
      setResultPage(payload.page || 1)
      setResultTotal(payload.total || 0)
      setResultPageSize(payload.pageSize || 25)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Falha ao carregar base global')
    } finally {
      setLoadingResults(false)
    }
  }

  const loadRuns = async () => {
    setLoadingRuns(true)
    try {
      const payload = await apiService.getZapLeadRuns({ page: 1, pageSize: 10 })
      setRuns(payload.items || [])
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Falha ao carregar histórico')
    } finally {
      setLoadingRuns(false)
    }
  }

  const loadRun = async (
    runId: number,
    page = resultPage,
    status = statusFilter,
    queue = queueFilter,
    signal = signalFilter,
    sort = prospectSort,
  ) => {
    try {
      const refreshedRun = await apiService.getZapLeadRun(runId)
      setCurrentRun(refreshedRun)
      await loadResults(runId, page, status, queue, signal, sort)
      return refreshedRun
    } catch {
      return null
    }
  }

  useEffect(() => {
    void loadRuns()
    const storedRunId = Number(localStorage.getItem(CURRENT_RUN_STORAGE_KEY))
    if (!Number.isInteger(storedRunId) || storedRunId <= 0) return
    void loadRun(storedRunId)
  }, [])

  useEffect(() => {
    if (resultScope !== 'run' || !currentRun || !['QUEUED', 'RUNNING'].includes(String(currentRun.status))) return
    const intervalId = window.setInterval(() => void loadRun(currentRun.runId), 6000)
    return () => window.clearInterval(intervalId)
  }, [currentRun?.runId, currentRun?.status, resultPage, resultScope, statusFilter, queueFilter, signalFilter, prospectSort])

  const handleSearch = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!activeFiltersCount) {
      toast.error('Informe ao menos um critério de localização')
      return
    }

    setSearching(true)
    setResultPage(1)
    try {
      const run = await apiService.searchZapLeads(buildSearchPayload())
      setCurrentRun(run)
      setResultScope('run')
      localStorage.setItem(CURRENT_RUN_STORAGE_KEY, String(run.runId))
      toast.success('Busca enviada com sucesso.')
      await loadResults(run.runId, 1, statusFilter, queueFilter, signalFilter, prospectSort)
      await loadRuns()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Falha ao iniciar a busca')
    } finally {
      setSearching(false)
    }
  }

  const handleRefresh = async () => {
    if (!currentRun) {
      toast.error('Busque antes de atualizar')
      return
    }
    setSearching(true)
    try {
      const refreshed = await loadRun(currentRun.runId)
      if (refreshed) toast.success('Execução atualizada')
    } finally {
      setSearching(false)
    }
  }

  const handleRerun = async () => {
    if (!currentRun) {
      toast.error('Busque antes de reexecutar')
      return
    }

    setSearching(true)
    try {
      const rerun = await apiService.rerunZapLeads(currentRun.runId, buildSearchPayload())
      setCurrentRun(rerun)
      setResultScope('run')
      localStorage.setItem(CURRENT_RUN_STORAGE_KEY, String(rerun.runId))
      setResultPage(1)
      await loadResults(rerun.runId, 1, statusFilter, queueFilter, signalFilter, prospectSort)
      await loadRuns()
      toast.success('Reexecução iniciada')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Falha no rerun')
    } finally {
      setSearching(false)
    }
  }

  const handleCancelRun = async () => {
    if (!currentRun || !['QUEUED', 'RUNNING'].includes(String(currentRun.status))) return
    setSearching(true)
    try {
      const cancelled = await apiService.cancelZapLeadsRun(currentRun.runId)
      setCurrentRun(cancelled)
      await loadRuns()
      toast.success('Execução interrompida')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Falha ao interromper execução')
    } finally {
      setSearching(false)
    }
  }

  const handleExport = async () => {
    setExporting(true)
    try {
      const csvText = await apiService.getZapLeadExportCsv(
        currentRun?.runId,
        statusFilter === 'all' ? undefined : statusFilter,
      )
      const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8' })
      const url = window.URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = currentRun ? `zap-leads-run-${currentRun.runId}.csv` : 'zap-leads-prospects.csv'
      anchor.style.display = 'none'
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Falha ao exportar CSV')
    } finally {
      setExporting(false)
    }
  }

  const handleMarkContacted = async (prospect: ZapLeadProspectItem) => {
    const note = window.prompt('Nota do contato', 'Contato comercial inicial.')
    if (note === null) return
    try {
      await apiService.updateZapLeadProspect(prospect.prospectId, {
        status: 'contatado',
        note,
        contactSource: 'Business Center',
      })
      if (currentRun) await loadResults(currentRun.runId, resultPage, statusFilter, queueFilter, signalFilter, prospectSort)
      toast.success('Prospect atualizado')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Falha ao atualizar prospect')
    }
  }

  const handleShowDetails = async (prospect: ZapLeadProspectItem) => {
    setLoadingProspectId(prospect.prospectId)
    try {
      setSelectedProspect(await apiService.getZapLeadProspect(prospect.prospectId))
      setActivityType('whatsapp')
      setActivityTemperature('')
      setActivityReason('')
      setActivityNextActionAt('')
      setActivityInviteLink('')
      setActivityNote('')
      setRegistrationTenantId('')
      setActivationFirstValueAction('primeiro_acesso')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Falha ao carregar detalhes')
    } finally {
      setLoadingProspectId(null)
    }
  }

  const handleLinkRegistration = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!selectedProspect) return

    const tenantIdText = registrationTenantId.trim()
    const tenantId = tenantIdText ? Number(tenantIdText) : null
    if (tenantIdText && (!Number.isInteger(tenantId) || Number(tenantId) <= 0)) {
      toast.error('Tenant inválido')
      return
    }

    setSavingRegistration(true)
    try {
      await apiService.updateZapLeadProspect(selectedProspect.prospectId, {
        status: 'cliente_existente',
        activityType: 'cadastro',
        isExistingTenant: true,
        existingTenantId: tenantId,
        note: 'Cadastro confirmado na Rede dos Corretores.',
        contactSource: 'Business Center',
      })

      if (resultScope === 'global') {
        await loadGlobalProspects(resultPage, statusFilter, queueFilter, signalFilter, prospectSort)
      } else if (currentRun) {
        await loadResults(currentRun.runId, resultPage, statusFilter, queueFilter, signalFilter, prospectSort)
      }

      setSelectedProspect(await apiService.getZapLeadProspect(selectedProspect.prospectId))
      toast.success('Cadastro vinculado')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Falha ao vincular cadastro')
    } finally {
      setSavingRegistration(false)
    }
  }

  const handleMarkActivation = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!selectedProspect) return

    setSavingActivation(true)
    try {
      await apiService.updateZapLeadProspect(selectedProspect.prospectId, {
        activityType: 'ativacao',
        firstValueAction: activationFirstValueAction,
        note: `Ativação registrada: ${activationActionLabel(activationFirstValueAction)}.`,
        contactSource: 'Business Center',
      })

      if (resultScope === 'global') {
        await loadGlobalProspects(resultPage, statusFilter, queueFilter, signalFilter, prospectSort)
      } else if (currentRun) {
        await loadResults(currentRun.runId, resultPage, statusFilter, queueFilter, signalFilter, prospectSort)
      }

      setSelectedProspect(await apiService.getZapLeadProspect(selectedProspect.prospectId))
      toast.success('Ativação registrada')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Falha ao registrar ativação')
    } finally {
      setSavingActivation(false)
    }
  }

  const handleSaveActivity = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!selectedProspect) return

    setSavingActivity(true)
    try {
      await apiService.updateZapLeadProspect(selectedProspect.prospectId, {
        status: activityType === 'nota' ? selectedProspect.status : 'contatado',
        activityType,
        ...(activityTemperature ? { temperature: activityTemperature } : {}),
        ...(activityReason ? { reason: activityReason } : {}),
        ...(activityNextActionAt ? { nextActionAt: activityNextActionAt } : {}),
        ...(activityInviteLink.trim() ? { inviteLink: activityInviteLink.trim() } : {}),
        ...(activityNote.trim() ? { note: activityNote.trim() } : {}),
        contactSource: 'Business Center',
      })

      if (resultScope === 'global') {
        await loadGlobalProspects(resultPage, statusFilter, queueFilter, signalFilter, prospectSort)
      } else if (currentRun) {
        await loadResults(currentRun.runId, resultPage, statusFilter, queueFilter, signalFilter, prospectSort)
      }

      setSelectedProspect(await apiService.getZapLeadProspect(selectedProspect.prospectId))
      setActivityNote('')
      setActivityNextActionAt('')
      setActivityInviteLink('')
      toast.success('Atividade registrada')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Falha ao registrar atividade')
    } finally {
      setSavingActivity(false)
    }
  }

  const updateStatusFilter = (next: ResultStatus) => {
    setStatusFilter(next)
    setResultPage(1)
    if (resultScope === 'global') {
      void loadGlobalProspects(1, next, queueFilter, signalFilter, prospectSort)
    } else if (currentRun) {
      void loadResults(currentRun.runId, 1, next, queueFilter, signalFilter, prospectSort)
    }
  }

  const updateQueueFilter = (next: QueueFilter) => {
    setQueueFilter(next)
    setResultPage(1)
    if (resultScope === 'global') {
      void loadGlobalProspects(1, statusFilter, next, signalFilter, prospectSort)
    } else if (currentRun) {
      void loadResults(currentRun.runId, 1, statusFilter, next, signalFilter, prospectSort)
    }
  }

  const updateSignalFilter = (next: SignalFilter) => {
    setSignalFilter(next)
    setResultPage(1)
    if (resultScope === 'global') {
      void loadGlobalProspects(1, statusFilter, queueFilter, next, prospectSort)
    } else if (currentRun) {
      void loadResults(currentRun.runId, 1, statusFilter, queueFilter, next, prospectSort)
    }
  }

  const updateProspectSort = (next: ZapLeadSort) => {
    setProspectSort(next)
    setResultPage(1)
    if (resultScope === 'global') {
      void loadGlobalProspects(1, statusFilter, queueFilter, signalFilter, next)
    } else if (currentRun) {
      void loadResults(currentRun.runId, 1, statusFilter, queueFilter, signalFilter, next)
    }
  }

  const openHistoryRun = async (runId: number) => {
    setResultPage(1)
    setResultScope('run')
    localStorage.setItem(CURRENT_RUN_STORAGE_KEY, String(runId))
    await loadRun(runId, 1, statusFilter, queueFilter)
  }

  const showRunResults = () => {
    setResultScope('run')
    setResultPage(1)
    if (currentRun) {
      void loadResults(currentRun.runId, 1, statusFilter, queueFilter, signalFilter, prospectSort)
    } else {
      setResults([])
      setResultTotal(0)
    }
  }

  const showGlobalResults = () => {
    setResultScope('global')
    setResultPage(1)
    void loadGlobalProspects(1, statusFilter, queueFilter, signalFilter, prospectSort)
  }

  const changeResultPage = (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalResultPages) return
    setResultPage(nextPage)
    if (resultScope === 'global') {
      void loadGlobalProspects(nextPage, statusFilter, queueFilter, signalFilter, prospectSort)
    } else if (currentRun) {
      void loadResults(currentRun.runId, nextPage, statusFilter, queueFilter, signalFilter, prospectSort)
    }
  }

  const currentRunError = errorSnapshotMessage(currentRun?.errorSnapshot)
  const resumeDelaySeconds = currentRun?.summary.enrichmentPending
    ? currentRun?.summary.enrichmentResumeDelaySeconds
    : currentRun?.summary.resumeDelaySeconds

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Leads ZAP</h1>
        <Button
          variant="outline"
          onClick={() => void handleExport()}
          disabled={exporting}
          className="gap-2"
        >
          <Download className="w-4 h-4" />
          {exporting ? 'Exportando...' : 'Exportar CSV'}
        </Button>
      </div>

      <section className="rounded-xl border bg-white p-5 shadow-sm space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Nova busca global</h2>
        <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <label className="space-y-1.5">
            <span className="text-sm text-gray-600">UF</span>
            <input className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={stateAcronym} onChange={(event) => setStateAcronym(event.target.value)} placeholder="GO" />
          </label>
          <label className="space-y-1.5">
            <span className="text-sm text-gray-600">Cidade</span>
            <input className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={addressCity} onChange={(event) => setAddressCity(event.target.value)} placeholder="Goiania" />
          </label>
          <label className="space-y-1.5">
            <span className="text-sm text-gray-600">Bairro</span>
            <input className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={addressNeighborhood} onChange={(event) => setAddressNeighborhood(event.target.value)} placeholder="Setor Bueno" />
          </label>
          <label className="space-y-1.5">
            <span className="text-sm text-gray-600">Zona</span>
            <input className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={addressZone} onChange={(event) => setAddressZone(event.target.value)} placeholder="Opcional" />
          </label>
          <label className="space-y-1.5">
            <span className="text-sm text-gray-600">Rua</span>
            <input className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={addressStreet} onChange={(event) => setAddressStreet(event.target.value)} placeholder="Opcional" />
          </label>
          <label className="space-y-1.5">
            <span className="text-sm text-gray-600">Tipo de imóvel</span>
            <select className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={unitType} onChange={(event) => setUnitType(event.target.value)}>
              <option value="APARTMENT">Apartamento</option>
              <option value="HOME">Casa</option>
              <option value="KITNET">Kitnet</option>
              <option value="COMMERCIAL_BUILDING">Comercial</option>
              <option value="COMMERCIAL_PROPERTY">Casa Comercial</option>
              <option value="PENTHOUSE">Cobertura</option>
            </select>
          </label>
          <label className="space-y-1.5">
            <span className="text-sm text-gray-600">Negócio</span>
            <select className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={business} onChange={(event) => setBusiness(event.target.value)}>
              <option value="SALE">Venda</option>
              <option value="RENTAL">Aluguel</option>
            </select>
          </label>
          <label className="space-y-1.5">
            <span className="text-sm text-gray-600">Tipo de anúncio</span>
            <select className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={listingType} onChange={(event) => setListingType(event.target.value)}>
              <option value="USED">Usado</option>
              <option value="DEVELOPMENT">Novo</option>
              <option value="ALL">Todos</option>
            </select>
          </label>
          <label className="space-y-1.5">
            <span className="text-sm text-gray-600">Preço mínimo</span>
            <input type="number" min={0} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={priceMin} onChange={(event) => setPriceMin(event.target.value)} />
          </label>
          <label className="space-y-1.5">
            <span className="text-sm text-gray-600">Preço máximo</span>
            <input type="number" min={0} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={priceMax} onChange={(event) => setPriceMax(event.target.value)} />
          </label>
          <label className="space-y-1.5">
            <span className="text-sm text-gray-600">Área mínima</span>
            <input type="number" min={0} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={usableAreaMin} onChange={(event) => setUsableAreaMin(event.target.value)} />
          </label>
          <label className="space-y-1.5">
            <span className="text-sm text-gray-600">Área máxima</span>
            <input type="number" min={0} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={usableAreaMax} onChange={(event) => setUsableAreaMax(event.target.value)} />
          </label>
          <label className="space-y-1.5">
            <span className="text-sm text-gray-600">Quartos</span>
            <input type="number" min={0} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={bedrooms} onChange={(event) => setBedrooms(event.target.value)} />
          </label>
          <label className="space-y-1.5">
            <span className="text-sm text-gray-600">Vagas</span>
            <input type="number" min={0} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={parkingSpaces} onChange={(event) => setParkingSpaces(event.target.value)} />
          </label>
          <label className="space-y-1.5">
            <span className="text-sm text-gray-600">Prospects desejados</span>
            <input type="number" min={1} max={1000} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={targetProspects} onChange={(event) => setTargetProspects(parseInt(event.target.value || '100', 10))} />
          </label>
          <label className="space-y-1.5">
            <span className="text-sm text-gray-600">Máximo de páginas</span>
            <input type="number" min={1} max={100} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={maxPages} onChange={(event) => setMaxPages(parseInt(event.target.value || '5', 10))} />
          </label>
          <label className="space-y-1.5">
            <span className="text-sm text-gray-600">Máximo de enriquecimentos</span>
            <input type="number" min={1} max={300} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={maxEnrichments} onChange={(event) => setMaxEnrichments(parseInt(event.target.value || '40', 10))} />
          </label>
          <div className="md:col-span-2 xl:col-span-3 pt-2">
            <Button type="submit" className="gap-2" disabled={searching || !activeFiltersCount}>
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Iniciar busca
            </Button>
          </div>
        </form>
      </section>

      <section className="rounded-xl border bg-white p-5 shadow-sm space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-gray-900">Historico de execucoes</h2>
          <Button size="sm" variant="outline" onClick={() => void loadRuns()} disabled={loadingRuns}>
            Atualizar
          </Button>
        </div>
        {loadingRuns ? (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            Carregando historico...
          </div>
        ) : runs.length === 0 ? (
          <p className="text-sm text-gray-500">Nenhuma execucao recente.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {runs.map((run) => (
              <button
                key={run.runId}
                type="button"
                onClick={() => void openHistoryRun(run.runId)}
                className="rounded-lg border border-gray-200 bg-white p-3 text-left hover:bg-gray-50"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-gray-900">Execucao #{run.runId}</span>
                  <span className="text-xs text-blue-700">{runStatusLabel(run.status)}</span>
                </div>
                <p className="mt-1 text-sm text-gray-600">{run.summary.totalProspects ?? run.summary.totalAdvertisers ?? 0} prospects</p>
                <p className="text-xs text-gray-500">{formatDateTime(run.updatedAt || run.createdAt)}</p>
              </button>
            ))}
          </div>
        )}
      </section>

      {currentRun && (
        <section className="rounded-xl border bg-white p-5 shadow-sm space-y-4">
          <div className="flex flex-wrap gap-3 items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Execução #{currentRun.runId}</h2>
            <div className="flex items-center gap-2">
              <span className="text-sm px-3 py-1 rounded-full border bg-blue-50 border-blue-100 text-blue-700">Status: {runStatusLabel(currentRun.status)}</span>
              <Button size="sm" variant="outline" onClick={handleRefresh} className="gap-2" disabled={searching}>
                <RefreshCw className="w-4 h-4" />
                Atualizar
              </Button>
              {['QUEUED', 'RUNNING'].includes(String(currentRun.status)) && (
                <Button size="sm" variant="outline" onClick={handleCancelRun} disabled={searching}>
                  Parar
                </Button>
              )}
              <Button size="sm" onClick={handleRerun} className="gap-2" disabled={searching}>
                <Search className="w-4 h-4" />
                Reexecutar
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 text-sm">
            <p><span className="text-gray-500">Criado em</span><br /><strong>{formatDateTime(currentRun.createdAt)}</strong></p>
            <p><span className="text-gray-500">Atualizado em</span><br /><strong>{formatDateTime(currentRun.updatedAt)}</strong></p>
            <p><span className="text-gray-500">Anúncios</span><br /><strong>{currentRun.summary.totalListings ?? 0}</strong></p>
            <p><span className="text-gray-500">Prospects</span><br /><strong>{currentRun.summary.totalProspects ?? currentRun.summary.totalAdvertisers ?? 0}</strong></p>
            <p><span className="text-gray-500">Duplicados</span><br /><strong>{currentRun.summary.duplicatesIgnored ?? 0}</strong></p>
            <p><span className="text-gray-500">Com contato</span><br /><strong>{currentRun.summary.prospectsWithContact ?? 0}</strong></p>
            <p><span className="text-gray-500">Com CRECI</span><br /><strong>{currentRun.summary.prospectsWithCreci ?? 0}</strong></p>
          </div>
          <div className="border-t border-gray-100 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 text-sm">
              <p><span className="text-gray-500">Páginas</span><br /><strong>{currentRun.summary.pagesProcessed ?? 0} paginas</strong></p>
              <p><span className="text-gray-500">Alvo</span><br /><strong>Alvo: {currentRun.summary.targetProspects ?? 0} prospects</strong></p>
              <p><span className="text-gray-500">Enriquecidos</span><br /><strong>Enriquecidos: {currentRun.summary.enrichedProspects ?? currentRun.summary.enrichedAdvertisers ?? 0}</strong></p>
              <p><span className="text-gray-500">Enriquecimento</span><br /><strong>{currentRun.summary.enrichmentPending ? 'Enriquecimento pendente' : 'Sem pendencia'}</strong></p>
              <p><span className="text-gray-500">Retomada</span><br /><strong>{resumeDelaySeconds ?? 0}s</strong></p>
              <p><span className="text-gray-500">Saúde da fonte</span><br /><strong>Saúde: {sourceHealthLabel(currentRun.summary.sourceHealth)}</strong><br /><span>{currentRun.summary.transientBlocks ?? 0} bloqueios · {currentRun.summary.transientBlockRate ?? 0}%</span></p>
            </div>
            {currentRunError && (
              <p className="mt-3 text-sm text-amber-700">
                Último erro operacional: {currentRunError}
              </p>
            )}
          </div>
        </section>
      )}

      <section className="rounded-xl border bg-white p-5 shadow-sm space-y-4">
        <div className="flex flex-wrap gap-2 items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Prospects</h2>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={showRunResults}
              type="button"
              className={`px-3 py-1.5 rounded-md text-sm border ${resultScope === 'run' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
            >
              Execucao atual
            </button>
            <button
              onClick={showGlobalResults}
              type="button"
              className={`px-3 py-1.5 rounded-md text-sm border ${resultScope === 'global' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
            >
              Base global
            </button>
            {STATUS_OPTIONS.map((option) => (
              <button
                key={option}
                onClick={() => updateStatusFilter(option)}
                type="button"
                className={`px-3 py-1.5 rounded-md text-sm border ${statusFilter === option ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
              >
                {statusLabel(option)}
              </button>
            ))}
            {QUEUE_OPTIONS.map((option) => (
              <button
                key={option}
                onClick={() => updateQueueFilter(option)}
                type="button"
                className={`px-3 py-1.5 rounded-md text-sm border ${queueFilter === option ? 'bg-purple-600 text-white border-purple-600' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
              >
                {option === 'all' ? 'Todas filas' : queueLabel(option)}
              </button>
            ))}
            {SIGNAL_OPTIONS.map((option) => (
              <button
                key={option}
                onClick={() => updateSignalFilter(option)}
                type="button"
                className={`px-3 py-1.5 rounded-md text-sm border ${signalFilter === option ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
              >
                {signalLabel(option)}
              </button>
            ))}
            <label className="inline-flex items-center gap-2 text-sm text-gray-600">
              Ordenar
              <select
                name="prospectSort"
                className="rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700"
                value={prospectSort}
                onChange={(event) => updateProspectSort(event.target.value as ZapLeadSort)}
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option} value={option}>{SORT_LABELS[option]}</option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {loadingResults ? (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            Carregando prospects...
          </div>
        ) : results.length === 0 ? (
          <div className="p-4 rounded-lg border border-dashed border-gray-300 text-sm text-gray-500">
            Nenhum prospect para este filtro.
          </div>
        ) : (
          <>
            <div className="overflow-auto -mx-5">
              <table className="w-full text-sm min-w-[920px]">
                <thead>
                  <tr className="text-left text-gray-600 border-b">
                    <th className="py-2 pr-3 font-semibold">Prospect</th>
                    <th className="py-2 pr-3 font-semibold">Contato</th>
                    <th className="py-2 pr-3 font-semibold">Status</th>
                    <th className="py-2 pr-3 font-semibold">Rede</th>
                    <th className="py-2 pr-3 font-semibold">Sinais</th>
                    <th className="py-2 font-semibold">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((prospect) => {
                    const whatsApp = whatsappHref(prospect.contactPhone)
                    const listingCount = prospect.signals?.listingCount ?? prospect.evidenceCount ?? 0
                    const hasContact = prospect.signals?.hasContact ?? Boolean(prospect.contactPhone || prospect.contactEmail)
                    const hasCreci = prospect.signals?.hasCreci ?? Boolean(prospect.licenseNumber)
                    const commercialScore = prospect.signals?.commercialScore
                    const suggestedQueue = prospect.signals?.suggestedQueue
                    const neighborhoods = prospect.signals?.neighborhoods || []
                    const unitTypes = prospect.signals?.unitTypes || []
                    const priceRange = formatPriceRange(prospect.signals?.priceRange)
                    return (
                      <tr key={prospect.prospectId} className="border-b last:border-b-0">
                        <td className="py-2 pr-3 align-top">
                          <p className="text-gray-900 font-medium">{prospect.name || '-'}</p>
                          <p className="text-xs text-gray-500">CRECI: {prospect.licenseNumber || '-'}</p>
                        </td>
                        <td className="py-2 pr-3 align-top">
                          {whatsApp ? (
                            <>
                              <a
                                className="inline-flex items-center gap-1.5 rounded-md border border-green-200 bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700 hover:bg-green-100"
                                href={whatsApp}
                                rel="noreferrer"
                                target="_blank"
                              >
                                <MessageCircle className="h-3.5 w-3.5" />
                                WhatsApp
                              </a>
                              <p className="mt-1 text-xs text-gray-500">{formatPhone(prospect.contactPhone)}</p>
                            </>
                          ) : (
                            <p className="text-gray-500">Sem WhatsApp</p>
                          )}
                        </td>
                        <td className="py-2 pr-3 align-top">{statusLabel(prospect.status)}</td>
                        <td className="py-2 pr-3 align-top">{prospect.isExistingTenant ? `Cadastrado #${prospect.existingTenantId ?? '-'}` : 'Não cadastrado'}</td>
                        <td className="py-2 pr-3 align-top">
                          <p>Score comercial: {typeof commercialScore === 'number' ? commercialScore : '-'}</p>
                          <p className="text-xs text-gray-500">Fila sugerida: {queueLabel(suggestedQueue)}</p>
                          <p className="text-xs text-gray-500">Evidências: {listingCount}</p>
                          <p className="text-xs text-gray-500">Com contato: {hasContact ? 'Sim' : 'Não'}</p>
                          <p className="text-xs text-gray-500">Com CRECI: {hasCreci ? 'Sim' : 'Não'}</p>
                          {neighborhoods.length > 0 && (
                            <p className="text-xs text-gray-500">Bairros: {neighborhoods.join(', ')}</p>
                          )}
                          {unitTypes.length > 0 && (
                            <p className="text-xs text-gray-500">Tipos: {unitTypes.join(', ')}</p>
                          )}
                          {priceRange && (
                            <p className="text-xs text-gray-500">Faixa: {priceRange}</p>
                          )}
                          {prospect.signals?.accountTier && (
                            <p className="text-xs text-gray-500">Tier: {prospect.signals.accountTier}</p>
                          )}
                          {typeof prospect.signals?.averageLqs === 'number' && (
                            <p className="text-xs text-gray-500">LQS médio: {Math.round(prospect.signals.averageLqs)}</p>
                          )}
                          {typeof prospect.qualityScore === 'number' && (
                            <p className="text-xs text-gray-500">Qualidade ZAP: {Math.round(prospect.qualityScore)}</p>
                          )}
                          {typeof prospect.portfolio?.totalListings === 'number' && (
                            <p className="text-xs text-gray-500">Carteira: {prospect.portfolio.totalListings}</p>
                          )}
                          <p className="text-xs text-gray-500">{portfolioStatusLabel(prospect.portfolio?.availabilityStatus)}</p>
                        </td>
                        <td className="py-2 align-top">
                          <div className="flex flex-col gap-2">
                            <Button size="sm" variant="outline" onClick={() => void handleShowDetails(prospect)} disabled={loadingProspectId === prospect.prospectId}>
                              {loadingProspectId === prospect.prospectId ? 'Carregando...' : 'Detalhes'}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => void handleMarkContacted(prospect)}>
                              Marcar contato
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            {selectedProspect && (
              <div className="border-t border-gray-200 pt-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-base font-semibold text-gray-900">Detalhes do prospect</h3>
                  <Button size="sm" variant="outline" onClick={() => setSelectedProspect(null)}>
                    Fechar
                  </Button>
                </div>

                <div className="mt-3 grid grid-cols-1 gap-3 text-sm md:grid-cols-2 lg:grid-cols-5">
                  <p><span className="text-gray-500">Prospect</span><br /><strong>{selectedProspect.name || '-'}</strong></p>
                  <p><span className="text-gray-500">CRECI</span><br /><strong>{selectedProspect.licenseNumber || '-'}</strong></p>
                  <p><span className="text-gray-500">Telefone</span><br /><strong>{formatPhone(selectedProspect.contactPhone)}</strong></p>
                  <p><span className="text-gray-500">E-mail</span><br /><strong>{selectedProspect.contactEmail || '-'}</strong></p>
                  <p><span className="text-gray-500">Ações realizadas</span><br /><strong>{activationActionsLabel(selectedProspect.activation?.actions)}</strong></p>
                </div>

                <form onSubmit={handleLinkRegistration} className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                  <h4 className="text-sm font-semibold text-gray-900">Cadastro na Rede</h4>
                  <div className="mt-3 flex flex-col gap-3 md:flex-row md:items-end">
                    <label className="space-y-1.5">
                      <span className="text-xs font-medium text-gray-600">ID do tenant</span>
                      <input
                        name="registrationTenantId"
                        type="number"
                        min={1}
                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm md:w-48"
                        value={registrationTenantId}
                        onChange={(event) => setRegistrationTenantId(event.target.value)}
                        placeholder="42"
                      />
                    </label>
                    <Button type="submit" size="sm" disabled={savingRegistration}>
                      {savingRegistration ? 'Vinculando...' : 'Marcar cadastro'}
                    </Button>
                  </div>
                </form>

                <form onSubmit={handleMarkActivation} className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <h4 className="text-sm font-semibold text-gray-900">Ativação inicial</h4>
                  <div className="mt-3 flex flex-col gap-3 md:flex-row md:items-end">
                    <label className="space-y-1.5">
                      <span className="text-xs font-medium text-gray-600">Primeira ação de valor</span>
                      <select
                        name="activationFirstValueAction"
                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm md:w-64"
                        value={activationFirstValueAction}
                        onChange={(event) => setActivationFirstValueAction(event.target.value)}
                      >
                        {ACTIVATION_ACTION_OPTIONS.map((option) => (
                          <option key={option} value={option}>{activationActionLabel(option)}</option>
                        ))}
                      </select>
                    </label>
                    <Button type="submit" size="sm" disabled={savingActivation}>
                      {savingActivation ? 'Salvando...' : 'Marcar ativação'}
                    </Button>
                  </div>
                </form>

                <form onSubmit={handleSaveActivity} className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <h4 className="text-sm font-semibold text-gray-900">Registro rápido</h4>
                  <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
                    <label className="space-y-1.5">
                      <span className="text-xs font-medium text-gray-600">Atividade</span>
                      <select
                        name="activityType"
                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                        value={activityType}
                        onChange={(event) => setActivityType(event.target.value as ZapLeadActivityType)}
                      >
                        {ACTIVITY_TYPE_OPTIONS.map((option) => (
                          <option key={option} value={option}>{activityTypeLabel(option)}</option>
                        ))}
                      </select>
                    </label>
                    <label className="space-y-1.5">
                      <span className="text-xs font-medium text-gray-600">Temperatura</span>
                      <select
                        name="activityTemperature"
                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                        value={activityTemperature}
                        onChange={(event) => setActivityTemperature(event.target.value)}
                      >
                        {TEMPERATURE_OPTIONS.map((option) => (
                          <option key={option || 'none'} value={option}>{option ? temperatureLabel(option) : 'Sem temperatura'}</option>
                        ))}
                      </select>
                    </label>
                    <label className="space-y-1.5">
                      <span className="text-xs font-medium text-gray-600">Motivo</span>
                      <select
                        name="activityReason"
                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                        value={activityReason}
                        onChange={(event) => setActivityReason(event.target.value)}
                      >
                        {REASON_OPTIONS.map((option) => (
                          <option key={option || 'none'} value={option}>{option ? reasonLabel(option) : 'Sem motivo'}</option>
                        ))}
                      </select>
                    </label>
                    <label className="space-y-1.5">
                      <span className="text-xs font-medium text-gray-600">Próxima tentativa</span>
                      <input
                        name="activityNextActionAt"
                        type="datetime-local"
                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                        value={activityNextActionAt}
                        onChange={(event) => setActivityNextActionAt(event.target.value)}
                      />
                    </label>
                    <label className="space-y-1.5">
                      <span className="text-xs font-medium text-gray-600">Link do convite</span>
                      <input
                        name="activityInviteLink"
                        type="url"
                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                        value={activityInviteLink}
                        onChange={(event) => setActivityInviteLink(event.target.value)}
                        placeholder="https://app.quadradois.com.br/register?token="
                      />
                    </label>
                  </div>
                  <label className="mt-3 block space-y-1.5">
                    <span className="text-xs font-medium text-gray-600">Nota</span>
                    <textarea
                      name="activityNote"
                      className="min-h-20 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                      value={activityNote}
                      onChange={(event) => setActivityNote(event.target.value)}
                      placeholder="Resumo curto da conversa ou da tentativa."
                    />
                  </label>
                  <div className="mt-3">
                    <Button type="submit" size="sm" disabled={savingActivity}>
                      {savingActivity ? 'Salvando...' : 'Salvar atividade'}
                    </Button>
                  </div>
                </form>

                <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Evidências</h4>
                    {selectedProspect.evidences?.length ? (
                      <ul className="mt-2 divide-y divide-gray-100 text-sm">
                        {selectedProspect.evidences.map((evidence) => (
                          <li key={evidence.id} className="py-2">
                            <p className="font-medium text-gray-900">{evidence.listing_id || '-'}</p>
                            <p className="text-gray-600">{evidenceLocation(evidence.city, evidence.neighborhood)}</p>
                            <p className="text-xs text-gray-500">Página: {evidence.search_page ?? '-'} • Status: {evidence.status || '-'}</p>
                            {evidence.enrichment_error && <p className="text-xs text-red-600">{evidence.enrichment_error}</p>}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mt-2 text-sm text-gray-500">-</p>
                    )}
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Atividades</h4>
                    {selectedProspect.activities?.length ? (
                      <ul className="mt-2 divide-y divide-gray-100 text-sm">
                        {selectedProspect.activities.map((activity, index) => (
                          <li key={`${activity.activityType}-${activity.createdAt}-${index}`} className="py-2">
                            <p className="font-medium text-gray-900">{activityTypeLabel(activity.activityType)}</p>
                            <p className="text-gray-700">{activity.note || '-'}</p>
                            {(activity.temperature || activity.reason || activity.nextActionAt) && (
                              <p className="text-xs text-gray-500">
                                Temperatura: {temperatureLabel(activity.temperature)} • Motivo: {reasonLabel(activity.reason)} • Próxima: {formatDateTime(activity.nextActionAt)}
                              </p>
                            )}
                            {activity.firstValueAction && (
                              <p className="text-xs text-gray-500">Ação de valor: {activationActionLabel(activity.firstValueAction)}</p>
                            )}
                            {activity.inviteLink && (
                              <p className="text-xs text-gray-500">Convite: {activity.inviteLink}</p>
                            )}
                            <p className="text-xs text-gray-500">{formatDateTime(activity.createdAt)}</p>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mt-2 text-sm text-gray-500">-</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between pt-2 text-sm text-gray-600">
              <p>Página {resultPage} de {totalResultPages || 1} • {resultTotal} prospects</p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => changeResultPage(Math.max(1, resultPage - 1))} disabled={resultPage <= 1}>
                  Anterior
                </Button>
                <Button variant="outline" size="sm" onClick={() => changeResultPage(resultPage + 1)} disabled={resultPage >= totalResultPages}>
                  Próxima
                </Button>
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  )
}
